import React, { useCallback, useMemo, useState } from 'react';

import styles from './superAdminHeUsers.module.scss';
import AddModal from './add-modal/AddModal';

import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import Button from '../../../components/button/Button';
import Search from '../../../components/search/Search';
import Spinner from '../../../components/spinner/Spinner';
import Pagination from '../../../components/pagination/Pagination';
import Table, { BodyColumn, HeaderColumn } from '../../../components/table/Table';
import IconPencil from '../../../assets/icons/pencil.svg?react';
import IconRotating from '../../../assets/icons/rotating_arrow.svg?react';
import { HEAUser } from '../../../types';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { useHeaUsersQuery } from '../../../api/graphql-codegen';
import { formatDate } from '../../../utils/time';

type Props = {};

function SuperAdminHeUsers(props: Props) {
  const { data, loading, refetch } = useHeaUsersQuery({
    notifyOnNetworkStatusChange: true,
  });
  const [perPage] = useState(50);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<HEAUser>();

  const handleRefetch = useCallback(() => refetch(), [refetch]);

  function filterTokens(search: string) {
    const s = search.toLowerCase();
    return (c: HEAUser) => {
      if (c.firstName && c.firstName.toLowerCase().includes(s)) return true;
      if (c.lastName && c.lastName.toLowerCase().includes(s)) return true;
      if (c.email && c.email.toLowerCase().includes(s)) return true;
      return false;
    };
  }

  const filter = useCallback((arr) => {
    return [...(arr || []).filter(filterTokens(search))];
  }, [search]);

  const { dataSlice: filteredData, onChangePage, totalFilteredItems, currentPage } = useClientPagination({
    data: data?.heaUsers || [],
    perPage,
    onFilter: filter,
  });

  const [showAddModal, setShowAddModal] = useState(false);

  const handleChangeSearch = useCallback((v) => {
    setSearch(v);
  }, [setSearch]);

  const handleChangePage = (page: number) => {
    onChangePage(page);
  };

  const handleAdd = useCallback(() => {
    setSelectedUser(undefined);
    setShowAddModal(true);
  }, []);

  const handleCloseModal = () => {
    setSelectedUser(undefined);
    setShowAddModal(false);
  };

  const handleEdit = useCallback((token: HEAUser) => () => {
    setSelectedUser(token);
    setShowAddModal(true);
  }, []);

  const handleResetFilters = useCallback(() => {
    setSearch('');
  }, []);

  const headerColumns: HeaderColumn[] = useMemo(() => {
    return [
      {
        title: 'First Name', sort: {
          key: 'firstName',
          asc: (a: HEAUser, b: HEAUser) => (a.firstName ?? '').localeCompare(b.firstName ?? ''),
          desc: (a: HEAUser, b: HEAUser) => (b.firstName ?? '').localeCompare(a.firstName ?? '')
        }
      }, {
        title: 'Last Name', sort: {
          key: 'lastName',
          asc: (a: HEAUser, b: HEAUser) => (a.lastName ?? '').localeCompare(b.lastName ?? ''),
          desc: (a: HEAUser, b: HEAUser) => (b.lastName ?? '').localeCompare(a.lastName ?? '')
        }
      }, {
        title: 'Email', sort: {
          key: 'email',
          asc: (a: HEAUser, b: HEAUser) => (a.email ?? '').localeCompare(b.email ?? ''),
          desc: (a: HEAUser, b: HEAUser) => (b.email ?? '').localeCompare(a.email ?? '')
        }
      }, {
        title: 'Association', sort: {
          key: 'association',
          asc: (a: HEAUser, b: HEAUser) => (a.association ?? '').localeCompare(b.association ?? ''),
          desc: (a: HEAUser, b: HEAUser) => (b.association ?? '').localeCompare(a.association ?? '')
        }
      }, {
        title: 'Study Plans', sort: {
          key: 'studyplanCount',
          asc: (a: HEAUser, b: HEAUser) => (a.studyplanCount ?? 0) - (b.studyplanCount ?? 0),
          desc: (a: HEAUser, b: HEAUser) => (b.studyplanCount ?? 0) - (a.studyplanCount ?? 0)
        }
      }, {
        title: 'Password', sort: {
          key: 'status',
          asc: (a: HEAUser, b: HEAUser) => (a.status?.toLowerCase() === 'passwordset' || a.status?.toLowerCase() === 'nameset' ? 'Yes' : 'No').localeCompare(b.status?.toLowerCase() === 'passwordset' || b.status?.toLowerCase() === 'nameset' ? 'Yes' : 'No'),
          desc: (a: HEAUser, b: HEAUser) => (b.status?.toLowerCase() === 'passwordset' || b.status?.toLowerCase() === 'nameset' ? 'Yes' : 'No').localeCompare(a.status?.toLowerCase() === 'passwordset' || a.status?.toLowerCase() === 'nameset' ? 'Yes' : 'No')
        }
      }, {
        title: 'Sign Up Date', sort: {
          key: 'registeredAt',
          asc: (a: HEAUser, b: HEAUser) => (a.registeredAt ?? '').localeCompare(b.registeredAt ?? ''),
          desc: (a: HEAUser, b: HEAUser) => (b.registeredAt ?? '').localeCompare(a.registeredAt ?? '')
        }
      }, {
        title: ''
      }
    ];
  }, []);

  const bodyColumns: BodyColumn[] = useMemo(() => {
    return [{
      key: (row: HEAUser) => {
        if (!(row?.firstName)) {
          return <div style={{color: "red"}}> - </div>
        }
        return <div>{row.firstName}</div>
      }, className: styles.firLas
    }, {
      key: (row: HEAUser) => {
        if (!(row?.lastName)) {
          return <div style={{color: "red"}}> - </div>
        }
        return <div>{row.lastName}</div>
      }, className: styles.firLas
    }, {
      key: (row: HEAUser) => {
        if (!(row?.email)) {
          return <div style={{color: "red"}}> - </div>
        }
        return <div>{row.email}</div>
      }, className: styles.columnAccSchEma
    }, {
      key: (row: HEAUser) => {
        if (!(row?.association)) {
          return <div style={{color: "red"}}> - </div>
        }
        return <div>{row.association}</div>
      }, className: styles.columnAccSchEma
    }, {
      key: (row: HEAUser) => {
        return <div>{row.studyplanCount ?? 0}</div>
      }, className: styles.columnAccSchEma
    }, {
      key: (row: HEAUser) => {
        const status = row.status?.toLowerCase() === 'passwordset' || row.status?.toLowerCase() === 'nameset'
        return (
          <div className={styles.statusWrapper}>
            <span className={`${styles.badge} ${status ? '' : styles.badgeBlue}`}>{status ? 'Yes' : 'No'}</span>
          </div>
        )
      }
    }, {
      key: (row: HEAUser) => {
        if (!(row?.registeredAt)) {
          return <div style={{color: "red"}}> - </div>
        }
        return <div>{formatDate(row.registeredAt)}</div>
      }, className: styles.columnAccSchEma
    }, {
      key: (row: HEAUser) => {
        return (
          <div style={{position: "relative"}}>
            <div className={styles.actionWrapper} onClick={handleEdit(row)}>
              <IconPencil className={styles.iconWrapper}/>
            </div>
          </div>
        )
      },
    }];
  }, [handleEdit]);

  return (
    <>
      <Header/>
      <div className={styles.page}>
        <div className={styles.header}>
          <h2 className={styles.title}>Home Ed Users</h2>
          <div>
            <Button onClick={handleAdd} className={styles.generateBtn} label={'Invite'}/>
          </div>
        </div>
        <div className={styles.search}>
          <Search onChange={handleChangeSearch} value={search}/>
          <div className={styles.filters}>
            <Button onClick={handleResetFilters} className={styles.resetBtn} label={'Reset'}
              size={'extraSmall'} outline={true} color={'green'}
              leftIcon={<IconRotating className={styles.resetIcon}/>}/>
          </div>
        </div>
        <div className={loading ? styles.content : ""}>
          {loading ? <Spinner/> : <>
            <Table color={'green'} header={headerColumns} body={bodyColumns} data={filteredData} noItemsText="No active users." />
            {data?.heaUsers && <div className={styles.paginationWrapper}>
              <Pagination currentPage={currentPage} perPage={perPage} totalItems={totalFilteredItems} onPageChange={handleChangePage}/>
            </div>}
          </>}
        </div>
      </div>
      <Footer/>
      <AddModal onCompleted={handleRefetch} open={showAddModal} onClose={handleCloseModal} user={selectedUser}/>
    </>
  );
}

export default SuperAdminHeUsers;
